import { useCallback, useEffect, useMemo, useRef } from "react";
import { recordAdBannerEvent } from "../api/adsApi";

// Count a display impression only when at least half of the rendered slot is
// continuously visible for one second. This matches the common display-ad
// viewability rule and, more importantly, prevents mounted/offscreen Virtuoso
// and Focus-neighbor slots from being reported as seen.
const VIEWABILITY_THRESHOLD = 0.5;
const VIEWABILITY_DWELL_MS = 1000;

// A module-level set survives React StrictMode effect replay and Virtuoso
// remounts during one browser page lifetime, while a full reload starts a new
// observation window. The slot key keeps two placements of the same creative
// distinct without storing a visitor/session identifier.
const shownKeys = new Set();
const failedKeys = new Set();

const toPositiveId = (value) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : 0;
};

const useAdBannerAnalytics = ({
  ad = null,
  houseAd = null,
  enabled = false,
  viewabilityEnabled = true,
  surface,
  device,
  placement,
  slotKey = "default",
}) => {
  const viewabilityRef = useRef(null);

  const descriptor = useMemo(() => {
    if (!enabled) return null;

    const campaignId = toPositiveId(ad?.campaignId);
    const creativeId = toPositiveId(ad?.creativeId);
    if (campaignId > 0 && creativeId > 0) {
      return {
        bannerType: "commercial",
        campaignId,
        creativeId,
        version: `${campaignId}:${creativeId}`,
      };
    }

    if (houseAd?.enabled === true) {
      return {
        bannerType: "house",
        campaignId: 0,
        creativeId: 0,
        version: String(houseAd.version || "default").trim() || "default",
      };
    }

    return null;
  }, [ad, enabled, houseAd]);

  const eventKey = useMemo(() => {
    if (!descriptor) return "";
    return [
      descriptor.bannerType,
      descriptor.version,
      surface,
      device,
      placement,
      slotKey,
    ].join(":");
  }, [descriptor, device, placement, slotKey, surface]);

  const recordShown = useCallback(() => {
    if (!descriptor || !eventKey || shownKeys.has(eventKey)) return;
    if (
      typeof document !== "undefined" &&
      document.visibilityState !== "visible"
    ) {
      return;
    }

    shownKeys.add(eventKey);
    recordAdBannerEvent({
      ...descriptor,
      event: "shown",
      surface,
      device,
      placement,
    });
  }, [descriptor, device, eventKey, placement, surface]);

  useEffect(() => {
    const element = viewabilityRef.current;

    if (
      !descriptor ||
      !eventKey ||
      !viewabilityEnabled ||
      !element ||
      shownKeys.has(eventKey) ||
      typeof IntersectionObserver === "undefined"
    ) {
      return undefined;
    }

    let dwellTimer = null;
    let currentIntersectionRatio = 0;

    const clearDwellTimer = () => {
      if (dwellTimer === null) return;
      window.clearTimeout(dwellTimer);
      dwellTimer = null;
    };

    const scheduleShown = () => {
      clearDwellTimer();

      if (
        shownKeys.has(eventKey) ||
        currentIntersectionRatio < VIEWABILITY_THRESHOLD ||
        (typeof document !== "undefined" &&
          document.visibilityState !== "visible")
      ) {
        return;
      }

      dwellTimer = window.setTimeout(() => {
        dwellTimer = null;

        if (
          shownKeys.has(eventKey) ||
          currentIntersectionRatio < VIEWABILITY_THRESHOLD ||
          (typeof document !== "undefined" &&
            document.visibilityState !== "visible")
        ) {
          return;
        }

        recordShown();
      }, VIEWABILITY_DWELL_MS);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.find((item) => item.target === element);
        currentIntersectionRatio = entry?.isIntersecting
          ? entry.intersectionRatio
          : 0;

        if (currentIntersectionRatio >= VIEWABILITY_THRESHOLD) {
          scheduleShown();
        } else {
          clearDwellTimer();
        }
      },
      {
        threshold: [0, VIEWABILITY_THRESHOLD, 1],
      }
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        clearDwellTimer();
        return;
      }

      if (currentIntersectionRatio >= VIEWABILITY_THRESHOLD) {
        scheduleShown();
      }
    };

    observer.observe(element);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearDwellTimer();
      observer.disconnect();
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [descriptor, eventKey, recordShown, viewabilityEnabled]);

  const onClick = useCallback(() => {
    if (!descriptor) return;
    recordAdBannerEvent({
      ...descriptor,
      event: "clicked",
      surface,
      device,
      placement,
    });
  }, [descriptor, device, placement, surface]);

  const onFailed = useCallback(() => {
    if (!descriptor || descriptor.bannerType !== "commercial" || !eventKey) return;
    if (failedKeys.has(eventKey)) return;
    failedKeys.add(eventKey);
    recordAdBannerEvent({
      ...descriptor,
      event: "failed",
      surface,
      device,
      placement,
    });
  }, [descriptor, device, eventKey, placement, surface]);

  return { viewabilityRef, onClick, onFailed };
};

export default useAdBannerAnalytics;
